import React from "react";
import { Icon, notification } from "antd";
import { makeAction } from "async-action-creator";
import jwtDecode from "jwt-decode";
import * as R from "ramda";

import i18n from "../../i18n";
import api from "../../utils/api";
import { createApiThunk } from "../../utils/thunk";
import history from "../../history";

import { fetchCompanyProfile } from "./company";
import { fetchCompanyPurchasesLite } from "./purchases";
import { fetchHeadquarter } from "./headquarter";
import {
  // fetchCountries,
  fetchProfessions,
  fetchTests,
  fetchUniversities,
  fetchStudies,
  fetchSkills
} from "./catalogs";
import { fetchUserProfile } from "./user";
import { fetchTeams } from "./teams";
import { fetchCompanies } from "./companies";

export const LOGIN = makeAction("session/LOGIN");
export const SIGNUP = makeAction("session/SIGNUP");
export const FETCH_INITIAL_DATA = makeAction("session/FETCH_INITIAL_DATA");
export const SEND_RECOVER_PASSWORD_EMAIL = makeAction(
  "session/SEND_RECOVER_PASSWORD_EMAIL"
);
export const CHANGE_RECOVER_PASSWORD = makeAction(
  "session/CHANGE_RECOVER_PASSWORD"
);
export const VALIDATE_RECOVER_PASSWORD_CODE = makeAction(
  "session/VALIDATE_RECOVER_PASSWORD_CODE"
);

const isExpiredRecoverPasswordCodeError = error => {
  const status = R.path(["response", "status"], error);
  const responseStatusCode = R.path(["response", "data", "statusCode"], error);
  const message = R.path(["response", "data", "message"], error);

  return (
    status === 410 ||
    responseStatusCode === 410 ||
    message === "invitation code has expired"
  );
};

const notifyExpiredRecoverPasswordCode = () =>
  notification.error({
    message: i18n.t("toasts:session.changeRecoverPasswordExpiredTitle"),
    description: i18n.t(
      "toasts:session.changeRecoverPasswordExpiredDescription"
    )
  });

export const login = createApiThunk({
  action: LOGIN,
  request: async formData => {
    const { token } = await api.session.login(formData);

    const { data } = jwtDecode(token);

    localStorage.setItem("token", token);

    return {
      ...data,
      token
    };
  },
  onResolved: () =>
    notification.success({
      message: i18n.t("toasts:session.loginResolvedTitle"),
      description: i18n.t("toasts:session.loginResolvedMessage")
    }),
  onRejected: ({ error }) => {
    const status = R.path(["response", "status"], error);

    return notification.error({
      message: i18n.t("global:error"),
      description:
        status === 401
          ? i18n.t("toasts:session.loginRejectedPassword")
          : i18n.t("toasts:session.loginRejectedToken")
    });
  }
});

export const logout = () => () => {
  localStorage.removeItem("token");
  localStorage.removeItem("selected_company_id");

  window.location.href = "/";
};

export const signup = createApiThunk({
  action: SIGNUP,
  request: formData => api.user.signup(formData),
  onResolved: () => {
    history.push("/login");
    notification.success({
      message: i18n.t("toasts:session.signupResolvedTitle")
    });
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("toasts:session.signupRejectedTitle")
    })
});

export const sendRecoverPasswordEmail = createApiThunk({
  action: SEND_RECOVER_PASSWORD_EMAIL,
  request: email => api.user.sendRecoverPasswordEmail(email),
  onResolved: () => {
    history.push("/login");
    notification.success({
      message: i18n.t(
        "components:PasswordRecoveryEmail.successNotificationTitle"
      ),
      description: i18n.t(
        "components:PasswordRecoveryEmail.successNotificationDescription"
      ),
      icon: <Icon type="mail" style={{ color: "#108ee9" }} />,
      duration: null
    });
  },
  onRejected: ({ error }) => {
    const status = R.path(["response", "status"], error);
    const message = R.path(["response", "data", "message"], error);

    return notification.error({
      message: i18n.t("toasts:session.sendRecoverPasswordEmailRejectedTitle"),
      description:
        status === 404 || message === "user_not_found"
          ? i18n.t("components:PasswordRecoveryEmail.userNotFound")
          : i18n.t("toasts:session.sendRecoverPasswordEmailRejectedDescription")
    });
  }
});

export const changeRecoverPassword = createApiThunk({
  action: CHANGE_RECOVER_PASSWORD,
  request: api.user.changeRecoverPassword,
  onResolved: () => {
    history.push("/login");
    notification.success({
      message: i18n.t("toasts:session.changeRecoverPasswordResolvedTitle")
    });
  },
  onRejected: ({ error }) => {
    const expiredCode = isExpiredRecoverPasswordCodeError(error);

    if (expiredCode) history.push("/login");

    if (expiredCode) return notifyExpiredRecoverPasswordCode();

    return notification.error({
      message: i18n.t("toasts:session.changeRecoverPasswordRejectedTitle")
    });
  }
});

export const validateRecoverPasswordCode = createApiThunk({
  action: VALIDATE_RECOVER_PASSWORD_CODE,
  request: api.user.validateRecoverPasswordCode,
  rejectOnError: true,
  onRejected: ({ error }) => {
    if (!isExpiredRecoverPasswordCodeError(error)) return;

    history.push("/login");
    return notifyExpiredRecoverPasswordCode();
  }
});

export const fetchInitialData = createApiThunk({
  action: FETCH_INITIAL_DATA,
  request: async (_, { session }, dispatch) => {
    const isSuperAdmin = session.roles.includes("headadmin");

    const company = isSuperAdmin
      ? await dispatch(fetchHeadquarter(session.company))
      : await dispatch(fetchCompanyProfile(session.company));

    if (!company || !company.id) {
      throw new Error("Unable to load the current company profile");
    }

    const storedCompanyId = localStorage.getItem("selected_company_id");
    if (storedCompanyId) await dispatch(fetchCompanyProfile(storedCompanyId));

    await dispatch(fetchUserProfile(company.id));

    if (isSuperAdmin) {
      dispatch(fetchUniversities(company.country));
      return Promise.all([
        dispatch(fetchCompanies()),
        dispatch(fetchProfessions(company.country)),
        dispatch(fetchSkills({ country: company.country })),
        // dispatch(fetchCountries()),
        dispatch(fetchStudies()),
        dispatch(fetchTests(company.id))
      ]);
    } else {
      dispatch(fetchUniversities(company.country));
      return Promise.all([
        dispatch(fetchProfessions(company.country)),
        dispatch(fetchSkills({ country: company.country })),
        // dispatch(fetchCountries()),
        dispatch(fetchStudies()),
        dispatch(fetchTeams()),
        dispatch(fetchTests(company.id)),
        dispatch(fetchCompanyPurchasesLite(company.id))
      ]);
    }
  },
  onRejected: ({ error, dispatch }) => {
    console.error("ERROR FETCHING INITIAL DATA:", error);

    dispatch(logout());

    notification.error({
      message: "Error",
      description:
        "Hubo un error obteniendo la informacion necesaria para iniciar sesión, intente de nuevo mas tarde"
    });
  }
});
